import { DateTime, Interval, Duration, Settings } from 'luxon';
import { formatRelative } from 'date-fns';
import i18n from './i18n';

const IS_TEST = process.env.NODE_ENV === 'test'; // eslint-disable-line

// for *most* date formatting functions, we'll use the same US English output
// TODO i18n - how to use locale-specific formatting?
Settings.defaultLocale = 'en-US';

export const jsDateToISO = (date) => {
  return DateTime.fromJSDate(date).toISO();
};

// September 29, 2022 at 11:05:47 a.m.
export const timestampFull = (ts) => {
  return DateTime.fromISO(ts).toLocal().toLocaleString({
    dateStyle: 'long',
    timeStyle: 'medium',
  });
};

// Sept 29, 2022
export const timestampShort = (ts) => {
  return DateTime.fromISO(ts).toLocal().toLocaleString({
    dateStyle: 'medium',
  });
};

// today at X, yesterday at Y
// luxon does not have a reasonable replacement for Moment's calendar()
// formatting function, so I had to pull in yet another library
export const timestampFriendly = (tsInMillis) => {
  const date = DateTime.fromMillis(tsInMillis).toLocal().toJSDate();
  return formatRelative(date, new Date());
};

// Thursday, September 29, 2022
export const timestampWithWeekday = (ts) => {
  return DateTime.fromISO(ts).toLocal().toLocaleString({
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

// 1:30 p.m.
export const timeOfDay = (ts) => {
  return DateTime.fromISO(ts).toLocal().toLocaleString({
    timeStyle: 'short',
  });
};

// split into date and time for input fields
export const splitDateTime = (ts) => {
  const d = (ts ? DateTime.fromISO(ts) : DateTime.now()).toLocal();
  return {
    exerciseDate: d.toISODate(),
    exerciseTime: d.toFormat('HH:mm'),
  };
};

// convert from HTML input values to ISO timestamp
// e.g. date = '2022-09-29', time = '13:30'
export const inputToISO = (date, time) => {
  const d = DateTime.fromISO(date);
  const t = DateTime.fromFormat(time, 'HH:mm');
  const dt = d.set({
    hour: t.hour,
    minute: t.minute
  }).toUTC();

  return dt.toISO();
};

// Mon, Tue, Wed, etc.
export const dayOfWeek = (ts) => {
  return DateTime.fromISO(ts).toLocal().toLocaleString({ weekday: 'short' });
};

// Sept 22
export const monthAndYear = (ts) => {
  return DateTime.fromISO(ts).toLocal().toLocaleString({ year: '2-digit', month: 'short' });
};

// { month: 'Sept', day: '22' }
export const shortDateObj = (ts) => {
  if (!ts) return {};

  const d = DateTime.fromISO(ts).toLocal();
  return {
    month: d.toLocaleString({ month: 'short' }),
    day: d.toLocaleString({ day: 'numeric' }),
  };
};

// The output of this function is ambiguous across locales (some do MMDD and others do DDMM).
// For this function only, use the system locale.
// e.g. 09-10, 10/9, 9/10
export const shortDate = (ts) => {

  // detect the system locale
  const locale = window.Intl && !IS_TEST ? 
    new window.Intl.DateTimeFormat().resolvedOptions().locale : 
    'en-US';

  return DateTime.fromISO(ts).toLocal().toLocaleString(
    { month: 'numeric', day: 'numeric' }, 
    { locale }
  );
};

export const currentMonthStr = () => {
  return DateTime.now().toLocaleString({ month: 'long' });
};

export const dateRange = (start, end) => {
  const s = DateTime.fromISO(start).toLocal();
  const e = DateTime.fromISO(end).toLocal();

  const sDate = s.toLocaleString({ month: 'short', day: 'numeric' });
  const sYear = s.toLocaleString({ year: 'numeric' });
  const eDate = e.toLocaleString({ month: 'short', day: 'numeric' });
  const eDayOnly = e.toLocaleString({ day: 'numeric' });

  if (s.year === e.year) {
    if (s.month === e.month) {
      return `${sDate} - ${eDayOnly}, ${sYear}`;
    } else {
      return `${sDate} - ${eDate}, ${sYear}`;
    }
  } else {
    return `${timestampShort(start)} - ${timestampShort(end)}`;
  }
};

export const getStreakEnd = (streak) => {
  const { num_weeks, start_date } = streak;
  const end_date = DateTime.fromISO(start_date).plus({ weeks: num_weeks }).minus({ days: 1 });
  return end_date.toISODate();
};

export const friendlyAge = (birthdate) => {
  const duration = Duration.fromMillis(
    DateTime.now().toMillis() - 
    DateTime.fromISO(birthdate).toMillis()
  ).shiftTo('months', 'years');

  if (duration.years === 0) {
    return i18n.t('common.months-old', { months: Math.floor(duration.months) });
  } else {
    return i18n.t('common.years-old', { years: Math.floor(duration.years) });
  }
};

export const friendlyCalendar = (ts, base) => {
  const b = (base ? DateTime.fromISO(base) : DateTime.now()).toLocal();
  const t = DateTime.fromISO(ts).toLocal();

  if (b.hasSame(t, 'day')) {
    return i18n.t('common.today');
  } else if (b.minus({ day: 1 }).hasSame(t, 'day')) {
    return i18n.t('common.yesterday');
  } else if (b.hasSame(t, 'week')) {
    return t.toLocaleString({ weekday: 'short' });
  } else {
    return shortDate(t.toISO());
  }

};

export function isInPast(unit, ts) {
  const now = DateTime.now().toLocal();
  const t = DateTime.fromISO(ts).toLocal();

  // assume week by default
  const start = DateTime.now().minus({ days: unit === 'month' ? 30 : 6 });

  const interval = Interval.fromDateTimes(start, now);
  return interval.contains(t);
}
